/**
 * Capability #8 — Trace ↔ visual evidence connection (compose-on-read).
 * Reuses 5A bands and URL references — never embeds screenshot blobs.
 */
import { z } from 'zod';

export const TraceVisualEvidenceBandSchema = z.enum(['available', 'limited', 'unavailable', 'none']);
export type TraceVisualEvidenceBand = z.infer<typeof TraceVisualEvidenceBandSchema>;

export const TraceVisualEvidenceGovernanceSchema = z.object({
  autoApply: z.literal(false),
  silentPublish: z.literal(false),
  recommendationsAreNotTruth: z.literal(true),
  advisoryOnly: z.literal(true),
  claimsApplicationDefect: z.literal(false),
  autoBlocksRelease: z.literal(false),
});
export type TraceVisualEvidenceGovernance = z.infer<typeof TraceVisualEvidenceGovernanceSchema>;

export const TraceVisualEvidenceItemSchema = z.object({
  stepId: z.string(),
  stepTitle: z.string(),
  status: z.enum(['match', 'mismatch', 'new_baseline', 'screenshot_only']),
  matchPercentage: z.number().min(0).max(100).nullable().optional(),
  threshold: z.number().min(0).max(100).nullable().optional(),
  /** Artifact URL references — not base64 payloads. */
  baselineUrl: z.string().nullable(),
  actualUrl: z.string().nullable(),
  diffUrl: z.string().nullable(),
  viewerHref: z.string().max(500),
});
export type TraceVisualEvidenceItem = z.infer<typeof TraceVisualEvidenceItemSchema>;

export const TraceVisualEvidenceDeepLinksSchema = z.object({
  visualDiffsHref: z.string().max(500),
  failureIntelligenceHref: z.string().max(500).nullable().optional(),
  changeImpactHref: z.string().max(500).nullable().optional(),
  releaseConfidenceHref: z.string().max(500).nullable().optional(),
});
export type TraceVisualEvidenceDeepLinks = z.infer<typeof TraceVisualEvidenceDeepLinksSchema>;

export const TraceVisualEvidenceResponseSchema = z.object({
  runId: z.string().uuid(),
  traceId: z.string().uuid().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  orgId: z.string().uuid(),
  commitSha: z.string().nullable().optional(),
  signal: z.literal('VISUAL_CHANGE_DETECTED'),
  band: TraceVisualEvidenceBandSchema,
  summary: z.string().max(500),
  why: z.array(z.string().max(300)).max(8).default([]),
  governance: TraceVisualEvidenceGovernanceSchema,
  items: z.array(TraceVisualEvidenceItemSchema).max(8),
  deepLinks: TraceVisualEvidenceDeepLinksSchema,
  referencesLoaded: z.number().int().nonnegative(),
  lazyLoaded: z.literal(true),
  queryCount: z.number().int().positive(),
  limits: z.object({
    maxReferences: z.number().int().positive(),
  }),
});
export type TraceVisualEvidenceResponse = z.infer<typeof TraceVisualEvidenceResponseSchema>;
